import { ReviewStep } from './ReviewSteps';

export type PopupAction = 'submit' | 'approve' | 'activate' | 'sync';

export interface PopupMode {
  primary: { label: string; action: PopupAction; isDisabled: boolean };
  // FleetOps staff approving groups awaiting review
  secondary?: { label: string; action: PopupAction };
  // Where the account is in setting up SSO. Not shown once SSO is active.
  step?: ReviewStep;
  // Shown next to the buttons: what happens when they're clicked
  hint?: string;
}

export const SUBMIT_HINT =
  'FleetOps reviews these groups next. Nothing changes for your users yet.';
export const IN_REVIEW_HINT =
  'FleetOps is reviewing these groups. Change them to resubmit.';
export const RESUBMIT_HINT = 'Your changes go to FleetOps for review.';
export const APPROVE_HINT =
  'Approving lets the customer activate SSO when they are ready.';
export const ACTIVATE_HINT =
  'Activating signs everyone out and switches to Microsoft sign-in.';
export const CHANGED_AFTER_APPROVAL_HINT =
  'Changing the groups sends them back to FleetOps for review.';

const getPendingReviewHint = ({
  hasChanges,
  canApprove,
}: {
  hasChanges: boolean;
  canApprove: boolean;
}) => {
  if (hasChanges) {
    return RESUBMIT_HINT;
  }
  return canApprove ? APPROVE_HINT : IN_REVIEW_HINT;
};

/**
 * The first sync is held for FleetOps review:
 * - The administrator submits the groups ("pending review").
 * - FleetOps staff approve them ("ready to activate").
 * - The administrator activates SSO when they're ready.
 * Once active, group changes sync immediately.
 */
const getPopupMode = ({
  status,
  hasChanges,
  canApprove,
}: {
  status: MicrosoftSso.MicrosoftConfig['status'];
  hasChanges: boolean;
  canApprove: boolean;
}): PopupMode => {
  if (status === 'active') {
    return {
      primary: {
        label: hasChanges ? 'Update SSO Config' : 'Sync Users',
        action: 'sync',
        isDisabled: false,
      },
    };
  }

  if (status === 'ready to activate') {
    if (hasChanges) {
      return {
        primary: {
          label: 'Resubmit for review',
          action: 'submit',
          isDisabled: false,
        },
        step: 3,
        hint: CHANGED_AFTER_APPROVAL_HINT,
      };
    }

    return {
      primary: { label: 'Activate SSO', action: 'activate', isDisabled: false },
      step: 3,
      hint: ACTIVATE_HINT,
    };
  }

  if (status === 'pending review') {
    return {
      primary: {
        label: 'Resubmit for review',
        action: 'submit',
        // Resubmitting the same groups would only raise another review ticket
        isDisabled: !hasChanges,
      },
      secondary:
        canApprove && !hasChanges
          ? { label: 'Approve', action: 'approve' }
          : undefined,
      step: 2,
      hint: getPendingReviewHint({ hasChanges, canApprove }),
    };
  }

  return {
    primary: {
      label: 'Submit for review',
      action: 'submit',
      isDisabled: false,
    },
    step: 1,
    hint: SUBMIT_HINT,
  };
};

export default getPopupMode;
